/* ═══════════════════════════════════════════════════════════════════════════
   🖼 services/attachFacts.js — what we know about a file somebody just attached.

   Twin of app/services/mediaFacts.ts. Same fields, same fail-soft rule, same
   reason for existing.

   🟥 A DOCUMENT SENT TO ALLO ARRIVED AS A GREY ICON while the identical file
      sent through a broadcast arrived with its FIRST PAGE showing — in the app,
      in this browser, and in the staff console. Not because the browser could
      not render one: `pdfFirstPageThumb` has done exactly that for the ordinary
      chat since documents were sendable. The support path simply never asked,
      and the server's sanitiser would have deleted the answer if it had.

   🟥 AND A CLIP ARRIVED AS A BLACK RECTANGLE, for the same reason.

   🟥 EVERY PATH HERE IS BEST-EFFORT AND FAILS TO `{}`. A poster is a
      convenience; a send that fails because a thumbnail could not be drawn is a
      feature holding a more important one hostage.
   ═══════════════════════════════════════════════════════════════════════════ */
import { pdfFirstPageThumb } from './documents';

/** Longest edge of a generated poster. Big enough for a card, small enough to upload. */
const MAX_EDGE = 480;

const fit = (w, h) => {
  if (!w || !h) return { w: MAX_EDGE, h: MAX_EDGE };
  const s = Math.min(1, MAX_EDGE / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
};

const toBlob = (canvas) => new Promise((res) => {
  try { canvas.toBlob(res, 'image/jpeg', 0.82); } catch { res(null); }
});

/** A picture's shape, without decoding it twice. */
export async function imageSize(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    return { width: img.naturalWidth || null, height: img.naturalHeight || null };
  } catch {
    return null;
  } finally {
    try { URL.revokeObjectURL(url); } catch { /* already released */ }
  }
}

/**
 * A still from a clip, plus its shape and how long it runs.
 *
 * 🟥 NOT FRAME ZERO. The first frame of a phone recording is very often black —
 *    a camera fading up, an autofocus hunt — so a poster taken from it is the
 *    black rectangle it was meant to replace.
 * 🟥 AND EVERY WAIT HAS A DEADLINE. A container the browser cannot demux fires
 *    neither `loadeddata` nor `error`, and the send would sit there for ever.
 */
export async function videoPoster(file) {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  try {
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    v.src = url;

    await new Promise((res, rej) => {
      const t = setTimeout(() => rej(new Error('timeout')), 8000);
      v.onloadeddata = () => { clearTimeout(t); res(); };
      v.onerror = () => { clearTimeout(t); rej(new Error('decode')); };
    });

    const durationSec = Number.isFinite(v.duration) && v.duration > 0 ? Math.round(v.duration) : null;

    await new Promise((res) => {
      const t = setTimeout(res, 3000);
      v.onseeked = () => { clearTimeout(t); res(); };
      try { v.currentTime = Math.min(1, (v.duration || 2) / 4); } catch { clearTimeout(t); res(); }
    });

    const { w, h } = fit(v.videoWidth, v.videoHeight);
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    c.getContext('2d').drawImage(v, 0, 0, w, h);
    return {
      blob: await toBlob(c),
      width: v.videoWidth || null,
      height: v.videoHeight || null,
      thumbWidth: w,
      thumbHeight: h,
      durationSec,
    };
  } catch {
    return null;
  } finally {
    try { v.src = ''; } catch { /* nothing to release */ }
    try { URL.revokeObjectURL(url); } catch { /* already released */ }
  }
}

/**
 * The facts about one attached file, with its poster uploaded.
 *
 * @param upload the same plaintext uploader the file itself goes through —
 *   passed in rather than imported, so this module has no opinion about where a
 *   support file lives and can be executed in a test without one.
 *
 * 🟥 THE POSTER IS PLAINTEXT, LIKE THE FILE. Every other thumbnail in Allo is
 *    sealed to a key the recipient holds; this chat has no such key by design,
 *    and the whole point is that somebody on the Allo team can look at it.
 */
export async function factsForSupport(file, kind, upload) {
  const out = {};
  if (!file) return out;
  if (Number.isFinite(Number(file.size))) out.size = Number(file.size);

  try {
    if (kind === 'image') {
      const size = await imageSize(file);
      if (size) { out.width = size.width; out.height = size.height; }
      return out;
    }

    if (kind === 'video') {
      const p = await videoPoster(file);
      if (!p) return out;
      out.width = p.width;
      out.height = p.height;
      out.durationSec = p.durationSec;
      if (p.blob) {
        const url = await upload(new File([p.blob], 'poster.jpg', { type: 'image/jpeg' }));
        if (url) { out.thumbUrl = url; out.thumbWidth = p.thumbWidth; out.thumbHeight = p.thumbHeight; }
      }
      return out;
    }

    if (kind === 'document') {
      const page = await pdfFirstPageThumb(file);
      if (page && page.bytes) {
        const url = await upload(new File([page.bytes], 'page1.png', { type: 'image/png' }));
        if (url) { out.thumbUrl = url; out.thumbWidth = page.width; out.thumbHeight = page.height; }
      }
      return out;
    }

    return out;
  } catch {
    /* 🟥 THE FILE STILL SENDS. Everything here is decoration on a message whose
       point is the bytes; losing the decoration must never lose the message. */
    return out;
  }
}
